// Custom select enhancer for .variant-option__select-wrapper
// Keeps the native <select> in the DOM so variant-picker.js works unchanged,
// while rendering a fully styleable button + dropdown on top.
// Re-initialises automatically after Idiomorph morphs remove the injected elements.

class CustomSelect {
  #wrapper;
  #native;
  #trigger;
  #list;
  #display;
  #isOpen = false;
  #selectObserver;

  constructor(wrapper) {
    this.#wrapper = wrapper;
    this.#native = wrapper.querySelector('select');
    if (!this.#native) return;
    this.#build();
    this.#bindEvents();
    this.#watchNative();
  }

  destroy() {
    this.#selectObserver?.disconnect();
  }

  #build() {
    Object.assign(this.#native.style, {
      position: 'absolute',
      opacity: '0',
      pointerEvents: 'none',
      width: '0',
      height: '0',
    });

    const existingIcon = this.#wrapper.querySelector('.icon');
    if (existingIcon) existingIcon.style.display = 'none';

    this.#trigger = document.createElement('button');
    this.#trigger.type = 'button';
    this.#trigger.className = 'cs-trigger';
    this.#trigger.setAttribute('data-cs', '');
    this.#trigger.setAttribute('aria-haspopup', 'listbox');
    this.#trigger.setAttribute('aria-expanded', 'false');

    this.#display = document.createElement('span');
    this.#display.className = 'cs-display';

    const caretEl = existingIcon ? existingIcon.cloneNode(true) : this.#makeCaret();
    caretEl.style.display = '';
    caretEl.setAttribute('aria-hidden', 'true');
    caretEl.setAttribute('data-cs', '');

    this.#trigger.append(this.#display, caretEl);

    this.#list = document.createElement('ul');
    this.#list.className = 'cs-dropdown';
    this.#list.setAttribute('role', 'listbox');
    this.#list.setAttribute('data-cs', '');
    this.#list.hidden = true;

    this.#wrapper.append(this.#trigger, this.#list);
    this.#renderOptions();
    this.#syncDisplay();

    // Associate trigger + listbox with the existing <label> for the native select
    if (this.#native.id) {
      const label = document.querySelector(`label[for="${CSS.escape(this.#native.id)}"]`);
      if (label) {
        if (!label.id) label.id = `cs-label-${Math.random().toString(36).slice(2, 7)}`;
        this.#trigger.setAttribute('aria-labelledby', label.id);
        this.#list.setAttribute('aria-labelledby', label.id);
      }
    }
  }

  #makeCaret() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 10 6');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M1 1l4 4 4-4');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-linecap', 'round');
    svg.append(path);
    return svg;
  }

  #renderOptions() {
    this.#list.innerHTML = '';
    for (const opt of this.#native.options) {
      if (opt.hidden) continue;
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      li.setAttribute('data-value', opt.value);
      li.setAttribute('aria-selected', String(opt.selected));
      li.textContent = opt.text;
      if (opt.selected) li.classList.add('cs-selected');
      this.#list.append(li);
    }
  }

  #syncDisplay() {
    const sel = this.#native.options[this.#native.selectedIndex];
    if (this.#display) this.#display.textContent = sel?.text ?? '';
  }

  #open() {
    this.#renderOptions();
    this.#isOpen = true;
    this.#list.hidden = false;
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#wrapper.classList.add('cs-open');
    this.#list.querySelector('.cs-selected, [role="option"]')?.focus();
  }

  #close() {
    this.#isOpen = false;
    this.#list.hidden = true;
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#wrapper.classList.remove('cs-open');
  }

  #pick(value) {
    this.#native.value = value;
    this.#native.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncDisplay();
    this.#close();
    this.#wrapper.classList.add('cs-loading');
  }

  #bindEvents() {
    this.#trigger.addEventListener('click', () => {
      this.#isOpen ? this.#close() : this.#open();
    });

    this.#trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#open();
      }
    });

    this.#list.addEventListener('click', (e) => {
      const li = e.target?.closest('[role="option"]');
      if (li instanceof HTMLElement) this.#pick(li.dataset.value ?? '');
    });

    this.#list.addEventListener('keydown', (e) => {
      const items = Array.from(this.#list.querySelectorAll('[role="option"]'));
      const idx = items.indexOf(document.activeElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          items[idx + 1]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (idx <= 0) { this.#close(); this.#trigger.focus(); }
          else items[idx - 1]?.focus();
          break;
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const item = items[idx];
          if (item instanceof HTMLElement) { this.#pick(item.dataset.value ?? ''); this.#trigger.focus(); }
          break;
        }
        case 'Escape':
          this.#close();
          this.#trigger.focus();
          break;
        case 'Tab':
          this.#close();
          break;
      }
    });

    document.addEventListener('pointerdown', (e) => {
      if (this.#isOpen && !this.#wrapper.contains(e.target)) this.#close();
    }, { capture: true });
  }

  #watchNative() {
    this.#selectObserver = new MutationObserver(() => {
      this.#syncDisplay();
      if (this.#isOpen) this.#renderOptions();
    });
    this.#selectObserver.observe(this.#native, {
      subtree: true,
      attributes: true,
      attributeFilter: ['selected', 'hidden'],
    });
  }
}

/** @type {WeakMap<Element, CustomSelect>} */
const instances = new WeakMap();

function initWrapper(wrapper) {
  if (wrapper.querySelector('[data-cs]')) return;
  instances.get(wrapper)?.destroy();
  instances.set(wrapper, new CustomSelect(wrapper));
}

new MutationObserver((mutations) => {
  for (const { type, target, removedNodes } of mutations) {
    if (type !== 'childList') continue;
    for (const node of removedNodes) {
      if (node instanceof Element && node.hasAttribute('data-cs')) {
        const wrapper =
          target instanceof Element && target.matches('.variant-option__select-wrapper')
            ? target
            : target instanceof Element
            ? target.closest('.variant-option__select-wrapper')
            : null;
        if (wrapper) { initWrapper(wrapper); break; }
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

document.querySelectorAll('.variant-option__select-wrapper').forEach(initWrapper);
